import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { useAppSelector } from "../../../../services/hooks";
import { isSuperAdminByRoleId } from "../../../../services/selectors/admin.selectors";
import styles from "../../appointmentsPage.module.css";
import { getStoredCalendarState } from "../../utils/calendarStorage";

type AppointmentTab = "calendar" | "therapists-availability" | "no-show-predictor" | "conflicts-detector";

interface ITabNavigationProps {
  className?: string;
}

/**
 * Tab Navigation Component
 *
 * Top-level navigation between Calendar, No-Show Predictor, and Conflicts Detector tabs.
 */
const TabNavigation: React.FC<ITabNavigationProps> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdminUser = useAppSelector(isSuperAdminByRoleId);

  // Determine active tab from URL
  const getActiveTab = (): AppointmentTab => {
    if (location.pathname.includes("/no-show-predictor"))
      return "no-show-predictor";
    if (location.pathname.includes("/conflicts-detector"))
      return "conflicts-detector";
    if (location.pathname.includes("/therapists-availability"))
      return "therapists-availability";
    return "calendar";
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tab: AppointmentTab) => {
    if (tab === "calendar") {
      const stored = getStoredCalendarState();
      const view = stored?.view ?? "daily";
      navigate(`/appointments/calendar/${view}`);
    } else if (tab === "therapists-availability") {
      navigate("/appointments/therapists-availability");
    } else if (tab === "no-show-predictor") {
      navigate("/appointments/no-show-predictor");
    } else {
      navigate("/appointments/conflicts-detector");
    }
  };

  const allTabs: {
    id: AppointmentTab;
    labelKey: string;
    icon: string;
    disabled?: boolean;
  }[] = [
    { id: "calendar", labelKey: "APPOINTMENTS_TAB_CALENDAR", icon: "📅" },
    {
      id: "therapists-availability",
      labelKey: "APPOINTMENTS_TAB_THERAPISTS_AVAILABILITY",
      icon: "👥",
    },
    {
      id: "no-show-predictor",
      labelKey: "APPOINTMENTS_TAB_NO_SHOW_PREDICTOR",
      icon: "🔮",
    },
    {
      id: "conflicts-detector",
      labelKey: "APPOINTMENTS_TAB_CONFLICTS_DETECTOR",
      icon: "🔍",
    },
  ];

  // Filter tabs: calendar + therapists-availability for all; super admin sees additional tabs
  const tabs = [
    allTabs.find((tab) => tab.id === "calendar")!,
    allTabs.find((tab) => tab.id === "therapists-availability")!,
    ...(isSuperAdminUser
      ? allTabs.filter(
          (tab) =>
            tab.id !== "calendar" && tab.id !== "therapists-availability"
        )
      : []),
  ];

  return (
    <div className={`${styles.tabNavigation} ${className || ""}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`${styles.tabButton} ${
            activeTab === tab.id ? styles.tabButtonActive : ""
          } ${tab.disabled ? styles.tabButtonDisabled : ""}`}
          onClick={() => !tab.disabled && handleTabChange(tab.id)}
          aria-selected={activeTab === tab.id}
          aria-disabled={tab.disabled}
          disabled={tab.disabled}
          role="tab"
        >
          <span className={styles.tabIcon}>{tab.icon}</span>
          <span className={styles.tabLabel}>{t(tab.labelKey)}</span>
        </button>
      ))}
    </div>
  );
};

export default TabNavigation;
